import type { UserUpload, UserUploadKind } from '@/types/user_upload';

export async function uploadUserImage(
  file: File,
  kind: UserUploadKind,
): Promise<UserUpload> {
  // 1. Get a presigned PUT URL for R2.
  const presignRes = await fetch('/api/user-uploads/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || 'image/png',
    }),
  });
  if (!presignRes.ok) {
    throw new Error(`Presign failed (${presignRes.status})`);
  }
  const { uploadUrl, publicUrl, storagePath } = (await presignRes.json()) as {
    uploadUrl: string;
    publicUrl: string;
    storagePath: string;
  };

  // 2. PUT the file directly to R2 — bypasses Vercel's 4.5MB body limit.
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'image/png' },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`R2 upload failed (${putRes.status})`);
  }

  // 3. Record the upload row in the DB.
  const dims = await readImageDimensions(file).catch(() => null);
  const recordRes = await fetch('/api/user-uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind,
      image_url: publicUrl,
      storage_path: storagePath,
      name: file.name,
      width: dims?.width ?? null,
      height: dims?.height ?? null,
    }),
  });
  if (!recordRes.ok) {
    throw new Error(`Save metadata failed (${recordRes.status})`);
  }
  const { upload } = (await recordRes.json()) as { upload: UserUpload };
  return upload;
}

function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };
    img.src = objectUrl;
  });
}
